import { useMemo, useState } from "react";
import { ChevronDown, Lock, Search, X } from "lucide-react";
import { CommitteeCard } from "@/components/tss/CommitteeCard";

const Status = ({ locked, chosen, current }) => {
  if (locked) return <span className="inline-flex items-center gap-1 text-forest"><Lock className="h-3 w-3" /> Final vote · {chosen?.name || "none"}</span>;
  if (chosen && current) return <span className="inline-flex items-center gap-1 text-forest"><Lock className="h-3 w-3" /> Current vote · {chosen.name}</span>;
  if (chosen) return <span className="text-maroon">Your pick · {chosen.name}</span>;
  return <span className="text-ink-muted">Optional · tap to choose</span>;
};

export const CategorySection = ({ cat, index, committees, open, onToggle, pick, votedFor, onPick, locked = false }) => {
  const [q, setQ] = useState("");
  const tid = cat.key || cat.id;
  const chosen = committees.find((c) => c.id === pick);
  const current = !!pick && pick === votedFor;
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? committees.filter((c) => `${c.name} ${c.area}`.toLowerCase().includes(s)) : committees;
  }, [q, committees]);

  return (
    <section
      id={`cat-${cat.id}`}
      data-testid={`category-section-${tid}`}
      className={`scroll-mt-24 overflow-hidden rounded-2xl border bg-white/90 transition-[border-color,box-shadow] duration-300 ${open ? "border-gold shadow-festive" : "border-gold/30"}`}
    >
      <button type="button" onClick={onToggle} data-testid={`category-toggle-${tid}`} className="flex w-full items-start gap-3 p-4 text-left">
        <span className={`mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full font-display text-sm font-bold ${locked || chosen ? "bg-forest text-ivory" : "bg-gold/20 text-maroon"}`}>
          {String(index + 1).padStart(2, "0")}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-display text-xl font-bold leading-tight text-maroon">{cat.name_en}</span>
          <span className="mt-0.5 block font-bengali text-[15px] text-ink/80">{cat.name_bn}</span>
          <span className="mt-1.5 block truncate text-xs font-medium" data-testid={`category-status-${tid}`}>
            <Status locked={locked} chosen={chosen} current={current} />
          </span>
        </span>
        <ChevronDown className={`mt-2 h-5 w-5 shrink-0 text-gold transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="rise px-4 pb-4">
          {locked ? (
            <p data-testid={`category-locked-${tid}`} className="rounded-xl bg-forest/10 p-3 text-sm text-forest">
              You have used all your vote changes. This is your final vote.
            </p>
          ) : (
            <>
              <label className="relative mb-3 block">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search puja or area…"
                  data-testid={`committee-search-${tid}`}
                  className="h-11 w-full rounded-full border border-gold/40 bg-ivory pl-9 pr-9 text-[15px] outline-none transition-[border-color,box-shadow] focus:border-gold focus:ring-2 focus:ring-gold/30"
                />
                {q && (
                  <button type="button" onClick={() => setQ("")} data-testid={`committee-search-clear-${tid}`} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted" aria-label="Clear search">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </label>
              <div className="space-y-2">
                {list.map((c) => (
                  <CommitteeCard key={c.id} committee={c} badgeIndex={committees.indexOf(c)} selected={pick === c.id} onSelect={() => onPick(c.id)} testId={`committee-card-${tid}-${c.id}`} />
                ))}
                {!list.length && <p className="py-6 text-center text-sm text-ink-muted" data-testid={`committee-empty-${tid}`}>No puja matches “{q}”.</p>}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
};
